import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Container,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  approveMcpAuthorizationRequestApi,
  denyMcpAuthorizationRequestApi,
  getMcpAuthorizationRequestApi,
} from "../../api/mcpOauth.api";
import BlinkoLogo from "../../design-library/BlinkoLogo";
import Button from "../../design-library/Button/Button.component";
// `l` only looks a key up; `Localize` also fills the {0}, {1}... placeholders
import Localize, { l } from "../../libs/localization";

// Consent screen of the MCP OAuth flow. An MCP client (e.g. Claude) sent the browser here
// with ?request=<id>; the user is already logged in (the router redirects to /login and back
// otherwise). Approving or denying sends the browser back to the client.

const SCOPE_KEYS = {
  "mcp:admin": "MCP_AUTHORIZE.SCOPE.MCP_ADMIN",
  "mcp:read": "MCP_AUTHORIZE.SCOPE.MCP_READ",
};

function McpAuthorizePage() {
  const theme = useTheme();
  const requestId = new URLSearchParams(window.location.search).get("request");

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!requestId) {
        setError(l("MCP_AUTHORIZE.INVALID_REQUEST"));
        setLoading(false);
        return;
      }
      try {
        const res = await getMcpAuthorizationRequestApi(requestId);
        if (!cancelled) setDetails(res);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(l("MCP_AUTHORIZE.INVALID_REQUEST"));
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  async function answer(apiCall) {
    setSubmitting(true);
    setError(null);
    try {
      const { redirectUrl } = await apiCall(requestId);
      // Leave the app: back to the MCP client with the authorization code (or the denial)
      window.location.assign(redirectUrl);
    } catch (err) {
      console.error(err);
      setError(l("MCP_AUTHORIZE.ERROR"));
      setSubmitting(false);
    }
  }

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        py: { xs: 0, sm: 8 },
        width: "100%",
        maxWidth: "none !important",
        backgroundColor: theme.palette.primary.light,
        minHeight: "100vh",
      }}
    >
      <Card
        sx={{
          width: { xs: "100%", sm: 560 },
          p: 6,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          boxShadow: "0px 4px 10px 0px #00000026",
        }}
      >
        <BlinkoLogo />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {details ? (
          <>
            <Typography variant="h5">
              {Localize("MCP_AUTHORIZE.TITLE", [details.clientName])}
            </Typography>
            <Typography variant="body1">
              {Localize("MCP_AUTHORIZE.DESCRIPTION", [
                details.clientName,
                details.resourceName,
                details.user.username,
              ])}
            </Typography>

            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {details.scopes.map((scope) => (
                <li key={scope}>
                  <Typography variant="body1">
                    {SCOPE_KEYS[scope] ? l(SCOPE_KEYS[scope]) : scope}
                  </Typography>
                </li>
              ))}
            </Box>

            <Typography variant="body2" sx={{ color: "#555" }}>
              {Localize("MCP_AUTHORIZE.REDIRECT_NOTICE", [
                details.redirectHost,
              ])}
            </Typography>
            <Typography variant="body2" sx={{ color: "#555" }}>
              {l("MCP_AUTHORIZE.TRUST_NOTICE")}
            </Typography>

            {!details.allowed ? (
              <Alert severity="warning">
                {details.reason || l("MCP_AUTHORIZE.NOT_ALLOWED")}
              </Alert>
            ) : null}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Button
                color="gray"
                disabled={submitting}
                onClick={() => answer(denyMcpAuthorizationRequestApi)}
              >
                {l("MCP_AUTHORIZE.DENY")}
              </Button>
              {details.allowed ? (
                <Button
                  disabled={submitting}
                  onClick={() => answer(approveMcpAuthorizationRequestApi)}
                >
                  {l("MCP_AUTHORIZE.APPROVE")}
                </Button>
              ) : null}
            </Box>
          </>
        ) : null}
      </Card>
    </Container>
  );
}

export default McpAuthorizePage;
